import React, { useEffect, useState, useMemo, lazy, Suspense } from "react";
import { Layout, Drawer, Modal } from "antd";
import {
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import { connect } from "react-redux";

// Eagerly load critical layout components
import TopNav from "../components/layout-components/TopNav";
import SideNav from "../components/layout-components/SideNav";
import CustomDrawer from "../components/common/Drawer";
import ScrollTop from "../components/util-components/ScrollTop";
import DocumentVerificationModal from "../components/DocumentVerificationModal";
import LogoIcon from "../components/common/LogoIcon";

import useWindowSize from "../hooks/useWindowSize";
import { authenticatedCompany, authenticatedUser } from "../helper/AuthHelper";
import { DOCUMENT_TYPES, VERIFICATION_STATUSES } from "../constants/Enums";
import { reduxFunctions } from "../helper/ReduxHelper";
import { navLinks } from "../configs/NavConfig";
import { config } from "../configs/app-config";
import "../styles/main.css";

// Code-split all route components (bundle-dynamic-imports)
const PastTrips = lazy(() => import("./PastTrips"));
const PayoutScreen = lazy(() => import("./PayoutScreen"));
const DriverList = lazy(() => import("./DriverListScreen"));
const VehicleList = lazy(() => import("./VehicleListScreen"));
const Vehicle = lazy(() => import("./Vehicle"));
const CompanyProfile = lazy(() => import("./CompanyProfile"));
const UserProfile = lazy(() => import("./UserProfile"));
const CalendarScreen = lazy(() => import("./CalendarScreen"));
const Insights = lazy(() => import("./Insights"));
const Dashboard = lazy(() => import("./Dashbaord"));
const UpcomingTrips = lazy(() => import("./UpcomingTrips"));
const LiveTrips = lazy(() => import("./LiveTrips"));
const LiveTrip = lazy(() => import("./LiveTrip"));
const Settings = lazy(() => import("./settings/index"));
const AddVehicle = lazy(() => import("./addVehicle"));
const PaymentHistoryScreen = lazy(() => import("./PaymentHistoryScreen"));
const TripSummary = lazy(() => import("./TripSummary"));
const DriverDetails = lazy(() => import("./DriverDetails"));
const AvailableTrips = lazy(() => import("./AvailableTrips"));
const AvailableDrivers = lazy(() => import("./AvailableDrivers"));
const SignupDriver = lazy(() => import("./SignupDriver"));
const Tracking = lazy(() => import("./tracking"));
const PendingBids = lazy(() => import("./bidding/PendingBids"));
const Wallet = lazy(() => import("./Wallet"));
const TripsWithPendingBids = lazy(
  () => import("./bidding/TripsWithPendingBids"),
);
const Trackers = lazy(() => import("./trackers/Trackers"));
const ViewTracker = lazy(() => import("./trackers/ViewTracker"));

const { Content } = Layout;

// Cache localStorage reads (js-cache-storage)
const SIDEBAR_KEY = "sidebar";
const getSidebarPreference = () => {
  try {
    return JSON.parse(localStorage.getItem(SIDEBAR_KEY));
  } catch {
    return null;
  }
};

// Delayed fallback to prevent flicker (rerender-usetransition-loading)
// Only shows loading state if it takes longer than 200ms
const DelayedFallback = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 200);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "80vh",
        backgroundColor: "#fff",
      }}
    >
      <LogoIcon />
    </div>
  );
};

// Wrapper for Suspense boundaries per route (async-suspense-boundaries)
const withSuspense = (Component) => (props) => (
  <Suspense fallback={<DelayedFallback />}>
    <Component {...props} />
  </Suspense>
);

const MainScreen = (props) => {
  // Lazy state initialization (rerender-lazy-state-init)
  const [collapsed, setCollapsed] = useState(() => {
    return getSidebarPreference() === "hide";
  });
  const [visible, setVisible] = useState(false);
  const [company, setCompany] = useState(() => authenticatedCompany());

  const size = useWindowSize();
  const { pathname } = useLocation();
  const history = useHistory();

  // Cache user data to avoid repeated calls (rerender-memo)
  const user = useMemo(() => authenticatedUser(), []);
  const userType = user?.user_type;
  const isStandAloneDriver = userType === "driver" && !user?.is_employee;

  // Derive page details instead of storing in state (rerender-derived-state-no-effect)
  const pageDetails = useMemo(() => {
    return navLinks.find((p) => p.path === pathname);
  }, [pathname]);

  // Derive CAC document (rerender-derived-state-no-effect)
  const cac_document = useMemo(() => {
    return company?.documents?.find(
      (el) => el.document_type === DOCUMENT_TYPES.CAC,
    );
  }, [company?.documents]);

  // Derive modal visibility (rerender-derived-state-no-effect)
  const showVerificationModal = useMemo(() => {
    return (
      cac_document?.status !== VERIFICATION_STATUSES.VERIFIED &&
      user?.user_type !== "driver"
    );
  }, [cac_document?.status, user?.user_type]);

  const showDrawer = () => setVisible(true);
  const onClose = () => setVisible(false);

  // Early exit pattern (js-early-exit)
  const toggle = () => {
    if (size.width <= 800) {
      setCollapsed(true);
      return;
    }

    // Functional setState for stable reference (rerender-functional-setstate)
    setCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem(
        SIDEBAR_KEY,
        JSON.stringify(newState ? "hide" : "show"),
      );
      return newState;
    });
  };

  // Permission check with primitive dependencies (rerender-dependencies)
  useEffect(() => {
    const hasPermission =
      pageDetails?.permission?.includes(userType) ||
      (pageDetails?.permission?.includes("stand-alone") && isStandAloneDriver);

    if (pageDetails && pageDetails.path === pathname && !hasPermission) {
      history.replace("/app/dashboard");
    }
  }, [pathname, pageDetails?.path, userType, isStandAloneDriver, history]);

  // Update company on Redux change
  useEffect(() => {
    setCompany(authenticatedCompany());
  }, [props.loadingCOMPANY]);

  return (
    <div>
      <Layout>
        <TopNav
          collapsed={collapsed}
          toggle={toggle}
          showDrawer={showDrawer}
          onClose={onClose}
          history={props.history}
        />
        <Content>
          <Layout>
            {size.width >= 1000 && (
              <SideNav collapsed={collapsed} location={props.location} />
            )}
            <Content className="main-content">
              <Switch>
                <Route
                  path="/app/signup-driver"
                  render={withSuspense(SignupDriver)}
                />
                <Route
                  path="/app/trips/past-trips"
                  render={withSuspense(PastTrips)}
                />
                <Route
                  path="/app/finance/wallet"
                  render={withSuspense(Wallet)}
                />
                <Route
                  path="/app/finance/payment-history"
                  render={withSuspense(PaymentHistoryScreen)}
                />
                <Route path="/app/insights" render={withSuspense(Insights)} />
                <Route
                  path="/app/finance/payouts"
                  render={withSuspense(PayoutScreen)}
                />
                <Route path="/app/profile" render={withSuspense(UserProfile)} />
                <Route
                  path="/app/profile-company"
                  render={withSuspense(CompanyProfile)}
                />
                <Route
                  path="/app/add-vehicle"
                  render={withSuspense(AddVehicle)}
                />
                <Route
                  path="/app/calendar"
                  render={withSuspense(CalendarScreen)}
                />
                <Route
                  path="/app/trips/upcoming-trips"
                  render={withSuspense(UpcomingTrips)}
                />
                <Route
                  path="/app/available-trips"
                  render={withSuspense(AvailableTrips)}
                />
                {config.features.bidding && (
                  <>
                    <Route
                      path="/app/pending-bids/:id"
                      render={withSuspense(PendingBids)}
                    />
                    <Route
                      path="/app/pending-bids"
                      render={withSuspense(TripsWithPendingBids)}
                    />
                  </>
                )}
                <Route
                  path="/app/available-drivers/:id"
                  render={withSuspense(AvailableDrivers)}
                />
                <Route
                  path="/app/trips/live-trips"
                  render={withSuspense(LiveTrips)}
                />
                <Route
                  path="/app/trips/offline-trip"
                  render={withSuspense(Tracking)}
                />
                <Route
                  path="/app/live-trip/:id"
                  render={withSuspense(LiveTrip)}
                />
                <Route
                  path="/app/trip-summary/:id"
                  render={withSuspense(TripSummary)}
                />
                <Route path="/app/vehicle/:id" render={withSuspense(Vehicle)} />
                <Route path="/app/settings" render={withSuspense(Settings)} />
                <Route
                  path="/app/driver-list"
                  render={withSuspense(DriverList)}
                />
                <Route
                  path="/app/driver/:id"
                  render={withSuspense(DriverDetails)}
                />
                <Route exact path="/app/trackers" render={withSuspense(Trackers)} />
                <Route
                  path="/app/trackers/:id"
                  render={withSuspense(ViewTracker)}
                />
                <Route
                  path="/app/vehicle-list"
                  render={withSuspense(VehicleList)}
                />
                <Route path="/app/dashboard" render={withSuspense(Dashboard)} />
                <Redirect from="/app" to="/app/dashboard" />
              </Switch>
            </Content>
          </Layout>
        </Content>
        <Drawer
          title="Menu"
          placement="left"
          closable={true}
          onClose={onClose}
          visible={visible}
        >
          <CustomDrawer onClose={onClose} />
        </Drawer>
        <ScrollTop />
      </Layout>

      <Modal
        visible={showVerificationModal}
        width={500}
        centered
        maskClosable={false}
        closable={false}
        footer={""}
      >
        <DocumentVerificationModal />
      </Modal>
    </div>
  );
};

const mapStateToProps = ({ loadingCOMPANY }) => ({ loadingCOMPANY });

const mapDispatchToProps = reduxFunctions;

export default connect(mapStateToProps, mapDispatchToProps)(MainScreen);
